import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import { cn, getYMargins, getSectionProps } from 'utils/styles';
import { ResourcesProps } from './types';
import { ResourceProps } from 'libs/contentful/types';
import Resource from './Resource';
import CTA from 'components/shared/CTA/CTA';
import { CardGrid } from 'components/shared/Card/CardGrid';
import ResourceSlider from './ResourcesSlider';
import KeyLine from 'components/shared/KeyLine';
import { KeyLinePosition, Margins } from 'types/constants';
import { liveUpdateAttrs } from 'libs/contentful/utils';

const Resources = ({ sys, sectionName, color, headline, margins, resources, cta, keyline }: ResourcesProps) => {
  const showTopKeyline = keyline === KeyLinePosition.Top && margins !== Margins.NoTop;
  const showBottomKeyLine = keyline === KeyLinePosition.Bottom && margins !== Margins.NoBottom;

  const wrapSlider = resources.items.length > 6;

  const slides = (
    <CardGrid
      className={cn(
        'xs:gap-x-6 gap-x-4 gap-y-8 auto-cols-[150px] grid-cols-[repeat(2_150px)]',
        wrapSlider && 'max-sm:grid-rows-2',
      )}>
      {resources?.items && resources.items.map(itemToResourceMap)}
    </CardGrid>
  );

  function headlineContent() {
    return (
      <div
        className="font-sans font-semibold text-heading-lg-mobile md:text-heading-lg md:mb-0 mb-8 block"
        {...liveUpdateAttrs(sys?.id, 'headline')}>
        <RichTextHeadline headline={headline} level="h3" />
      </div>
    );
  }

  function ctaButton() {
    return (
      <div className="w-fit mt-8 md:mt-0">
        <CTA cta={cta} variant="outline" />
      </div>
    );
  }

  function resourcesSlider() {
    return (
      <div className="md:hidden">
        <ResourceSlider wrapSlider={wrapSlider} color={color} numberOfCards={resources.items.length}>
          {slides}
        </ResourceSlider>
        {cta && ctaButton()}
      </div>
    );
  }

  return (
    <section {...getSectionProps(color, undefined, sectionName)}>
      <div className="content-wrapper">
        {showTopKeyline && <KeyLine />}
        <div className={`${getYMargins(margins)} md:flex md:flex-row md:gap-6`}>
          {(headline || cta) && (
            <div className="flex flex-col gap-5 w-[314px] shrink-0">
              {headline && headlineContent()}
              {cta && <div className="hidden md:block">{ctaButton()}</div>}
            </div>
          )}
          {resourcesSlider()}
          <div className={`hidden md:flex flex-row gap-6`}>
            <div
              className="grid ml:grid-cols-3 md:grid-cols-2 auto-rows-auto gap-6"
              {...liveUpdateAttrs(sys?.id, 'content')}>
              {resources?.items && resources.items.map(itemToResourceMap)}
            </div>
          </div>
        </div>
        {showBottomKeyLine && <KeyLine />}
      </div>
    </section>
  );
};

const itemToResourceMap = (resource: ResourceProps, index: number) => {
  return (
    <Resource
      sys={resource.sys}
      key={resource.sys?.id ? `${resource.sys.id}_${index}` : `resource_${index}`}
      index={index}
      resourceIcon={resource.resourceIcon}
      resourceLabelRte={resource.resourceLabelRte}
      resourceCopy={resource.resourceCopy}
      resourcePage={resource.resourcePage}
      url={resource.url}
      aspectRatio={resource.aspectRatio}
    />
  );
};

export default Resources;
